/**
 * 認証サービス - ビジネスロジックとAPI呼び出しの統合
 * UI層とAPI層の橋渡し役として機能
 * エラーハンドリング、リトライ、開発環境フォールバックなどを担当
 */

import { authApiClient } from './authApiClient.js';
import { 
  validateField, 
  getErrorMessage, 
  ERROR_MESSAGES,
  SUCCESS_MESSAGES 
} from '../utils/authUtils.js';

/**
 * 認証サービスクラス
 * ビジネスルールの適用、エラーハンドリング、開発環境対応を担当
 */
class AuthService {
  constructor(apiClient = authApiClient) {
    this.apiClient = apiClient;
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  /**
   * API呼び出しをラップし、開発環境フォールバックとエラーハンドリングを提供
   * @private
   * @param {Function} apiCall - API呼び出し関数
   * @param {Function} mockFallback - 開発環境フォールバック関数
   * @param {string} action - アクション名
   * @returns {Promise<any>} API結果
   */
  async _executeWithFallback(apiCall, mockFallback, action) {
    try {
      const result = await apiCall();
      return result;
    } catch (error) {
      console.error(`[AUTH SERVICE] ${action} - Error:`, error.message);
      
      // 開発環境でのフォールバック
      if (this.isDevelopment) {
        return mockFallback();
      }
      
      throw error;
    }
  }

  /**
   * 認証コード送信
   * バリデーション + API呼び出し + エラーハンドリング
   * @param {string} email - メールアドレス
   * @returns {Promise<Object>} 実行結果
   */
async sendVerificationCode(email) {
  // 入力値バリデーション
  const validationError = validateField('email', email);
  if (validationError) {
    return { success: false, error: validationError, type: 'validation' };
  }

  try {
    const success = await this._executeWithFallback(
      () => this.apiClient.sendVerificationCode(email),
      () => {
        return true;
      },
      `Send verification code to: ${email}`
    );

    return { success, message: SUCCESS_MESSAGES.CODE_SENT, type: 'success' };
  } catch (error) {
    // バックエンドから返ってきたメッセージを優先、なければ汎用メッセージ
    const errorMessage = error.message || getErrorMessage(error);
    return { success: false, error: errorMessage, type: 'api', originalError: error };
  }
}

  /**
   * 認証コード検証
   * バリデーション + API呼び出し + エラーハンドリング
   * @param {string} email - メールアドレス
   * @param {string} code - 認証コード
   * @returns {Promise<Object>} 検証結果
   */
  async verifyCode(email, code) {
  const emailError = validateField('email', email);
  if (emailError) return { success: false, error: emailError, type: 'validation' };
  
  const codeError = validateField('verificationCode', code);
  if (codeError) return { success: false, error: codeError, type: 'validation' };

  try {
    const success = await this._executeWithFallback(
      () => this.apiClient.verifyCode(email, code),
      () => {
          // 開発環境でもAPIの認証結果に従う（固定コードを受け入れない）
          throw new Error('API not available in development mode');
        },
        `Verify code: ${code} for email: ${email}`
    );

    return success 
      ? { success: true, message: SUCCESS_MESSAGES.AUTH_COMPLETE, type: 'success' }
      : { success: false, error: ERROR_MESSAGES.VERIFY_FAILED, type: 'verification' };
  } catch (error) {
    // バックエンドから返ってきたメッセージを優先、なければ汎用メッセージ
    const errorMessage = error.message || getErrorMessage(error);
    return { success: false, error: errorMessage, type: 'api', originalError: error };
  }
}

  /**
   * 認証コード再送信
   * 基本的には送信と同じだが、メッセージを変更
   * @param {string} email - メールアドレス
   * @returns {Promise<Object>} 実行結果
   */
  async resendVerificationCode(email) {
    const result = await this.sendVerificationCode(email);
    
    if (result.success) {
      return {
        ...result,
        message: SUCCESS_MESSAGES.CODE_RESENT
      };
    }

    return {
      ...result,
      error: result.error || ERROR_MESSAGES.RESEND_FAILED
    };
  }

  /**
   * ログアウト
   * @param {string} token - アクセストークン
   * @returns {Promise<Object>} 実行結果
   */
  async logout(token) {
    try {
      const success = await this._executeWithFallback(
        () => this.apiClient.logout(token),
        () => true, // 開発環境では常に成功
        `Logout with token: ${token}`
      );

      return {
        success,
        message: success ? 'ログアウトしました' : 'ログアウトに失敗しました',
        type: success ? 'success' : 'error'
      };
    } catch (error) {
      // バックエンドから返ってきたメッセージを優先、なければ汎用メッセージ
      const errorMessage = error.message || getErrorMessage(error);
      return {
        success: false,
        error: errorMessage,
        type: 'api',
        originalError: error
      };
    }
  }

  /**
   * APIヘルスチェック
   * @returns {Promise<Object>} ヘルスチェック結果
   */
  async checkHealth() {
    try {
      const isHealthy = await this.apiClient.healthCheck();
      return {
        healthy: isHealthy,
        message: isHealthy ? 'API is healthy' : 'API is not responding',
        timestamp: new Date().toLocaleDateString('sv-SE')
      };
    } catch (error) {
      return {
        healthy: false,
        message: 'Health check failed',
        error: error.message,
        timestamp: new Date().toLocaleDateString('sv-SE')
      };
    }
  }

  /**
   * 設定情報の取得
   * @returns {Object} API設定とサービス情報
   */
  getServiceInfo() {
    return {
      apiConfig: this.apiClient.getConfig(),
      isDevelopment: this.isDevelopment,
      environment: this.apiClient.getEnvironment(),
      baseUrl: this.apiClient.getBaseUrl()
    };
  }

  /**
   * API設定を更新
   * @param {Object} newConfig - 新しい設定
   */
  updateConfig(newConfig) {
    this.apiClient.updateConfig(newConfig);
  }

  /**
   * 認証フロー全体の実行（メールアドレス入力 → コード送信 → コード検証）
   * @param {string} email - メールアドレス
   * @param {string} code - 認証コード（オプション、未指定なら送信のみ）
   * @returns {Promise<Object>} 実行結果
   */
  async executeAuthFlow(email, code = null) {
    // Step 1: コード送信
    const sendResult = await this.sendVerificationCode(email);
    if (!sendResult.success) {
      return sendResult;
    }

    // コードが指定されていない場合はここで終了
    if (!code) {
      return sendResult;
    }

    // Step 2: コード検証
    const verifyResult = await this.verifyCode(email, code);
    return verifyResult;
  }
}

// ■ インスタンス作成関数群

/**
 * デフォルト設定でサービスインスタンスを作成
 * @returns {AuthService} デフォルトサービス
 */
export const createAuthService = () => {
  return new AuthService();
};

/**
 * カスタムAPIクライアントでサービスを作成
 * @param {AuthApiClient} apiClient - カスタムAPIクライアント
 * @returns {AuthService} カスタムサービス
 */
export const createAuthServiceWithClient = (apiClient) => {
  return new AuthService(apiClient);
};

// ■ デフォルトインスタンス（シングルトン）
export const authService = createAuthService();

// ■ 便利な関数エクスポート（UIコンポーネントから直接使用可能）

/**
 * 認証コード送信（関数スタイル）
 * @param {string} email - メールアドレス
 * @returns {Promise<Object>} 実行結果
 */
export const sendVerificationCode = (email) => authService.sendVerificationCode(email);

/**
 * 認証コード検証（関数スタイル）
 * @param {string} email - メールアドレス
 * @param {string} code - 認証コード
 * @returns {Promise<Object>} 検証結果
 */
export const verifyCode = (email, code) => authService.verifyCode(email, code);

/**
 * 認証コード再送信（関数スタイル）
 * @param {string} email - メールアドレス
 * @returns {Promise<Object>} 実行結果
 */
export const resendVerificationCode = (email) => authService.resendVerificationCode(email);

export default AuthService;